import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store/hooks";
import {
  addWeaponMount,
  removeWeaponMount,
  updateWeaponMount,
} from "../../../../store/features/shipBuilderSlice";
import { WeaponMount, Weapon } from "../../../../types/weapons";
import { WeaponRange } from "../../../../types/weapons";
import { WEAPON_TYPES, AMMO_TYPES } from "../../../../constants/ship";
import { formatCurrency } from "../../../../utils/formatting";
import {
  calculateHardpoints,
  calculateFirmpoints,
  usesFirmpoints,
  getMountConfig,
  getFirmpointRangeLimit,
  calculateFirmpointPowerReduction,
  canUpgradeMountToTurret,
} from "../../../../utils/weaponMountCalculations";
import { selectors } from "../../../../store/selectors/shipBuilderSelectors";
import { validateSpinalWeaponInstallation, calculateSpinalWeaponStats } from "../../../../utils/calculations/weaponCalculations";
import WeaponSelector from "./WeaponSelector";

const BulkWeaponAssignment: React.FC = () => {
  const dispatch = useAppDispatch();
  const hullSize = useAppSelector((state) => state.shipBuilder.shipSize);
  const shipTechLevel = useAppSelector((state) => state.shipBuilder.techLevel);
  const weaponMounts =
    useAppSelector((state) => state.shipBuilder.weaponMounts) || [];
  const weightOverrideActive = useAppSelector(selectors.selectOverrideWeight);

  const [selectedMountType, setSelectedMountType] = useState<string>("");

  // Auto-add mount when mount type is selected
  const handleMountTypeChange = (mountType: string) => {
    setSelectedMountType(mountType);
    
    if (mountType) {
      // Create a basic mount with default configuration
      const mountConfig = getMountConfig(mountType, "single", hullSize);
      
      const mount: WeaponMount = {
        type: mountType,
        turretType: mountType === "turret" ? "single" : undefined,
        weapons: [], // Start with no weapons
        hardpointsUsed: mountConfig.hardpointsUsed,
        firmpointsUsed: mountConfig.firmpointsUsed,
        range: "Close",
        power: 0, // Will be calculated when weapons are added
        tons: mountConfig.tons, // Mount tonnage (fixed)
        cost: mountConfig.cost,
        maxWeapons: mountConfig.maxWeapons,
        quantity: 1, // Default to 1 mount
      };

      // Add the mount to the store
      dispatch(addWeaponMount(mount));
      
      // Reset the dropdown
      setSelectedMountType("");
    }
  };

  // Calculate available hardpoints and firmpoints using new utility functions
  const hardpoints = calculateHardpoints(hullSize);
  const firmpoints = calculateFirmpoints(hullSize);
  const shipUsesFirmpointsForShip = usesFirmpoints(hullSize);

  const hardpointsUsed = weaponMounts.reduce(
    (total, mount) => total + (mount.hardpointsUsed || 0) * (mount.quantity || 1),
    0,
  );
  const firmpointsUsed = weaponMounts.reduce(
    (total, mount) => total + (mount.firmpointsUsed || 0) * (mount.quantity || 1),
    0,
  );

  const availableHardpoints = hardpoints - hardpointsUsed;
  const availableFirmpoints = firmpoints - firmpointsUsed;

  // Count how many Firmpoints have been upgraded to turrets
  const firmpointUpgrades = weaponMounts.filter(mount => 
    mount.isFirmpointUpgrade === true
  ).length;

  const getMountTypeDisplay = (type: string) => {
    switch (type) {
      case "fixed":
        return "Fixed Mount";
      case "turret":
        return "Turret";
      case "barbette":
        return "Barbette";
      case "smallBay":
        return "Small Bay";
      case "mediumBay":
        return "Medium Bay";
      case "largeBay":
        return "Large Bay";
      case "spinalMount":
        return "Spinal Mount";
      default:
        return type;
    }
  };

  const handleUpgradeFirmpointToTurret = (mountIndex: number) => {
    const mount = weaponMounts[mountIndex];
    if (!mount || mount.type !== "fixed") return;

    // Check if upgrade is allowed
    if (!canUpgradeMountToTurret(mount.type, hullSize, firmpointUpgrades)) {
      alert("Only one Firmpoint can be upgraded to a turret on small craft.");
          return;
    }

    // Create upgraded turret mount
    const upgradedMount: WeaponMount = {
      ...mount,
      type: "turret",
      turretType: "single", // Only single turrets allowed for Firmpoint upgrades
      isFirmpointUpgrade: true,
      hardpointsUsed: 0, // Turret uses no hardpoints on small craft
      firmpointsUsed: 1, // Still uses 1 Firmpoint
      tons: mount.tons + 1, // Turret adds 1 ton
      cost: mount.cost + 200000, // Add turret cost (MCr0.2)
      maxWeapons: 1, // Single turret can only hold 1 weapon
    };

    // Update the mount
    dispatch(updateWeaponMount({ index: mountIndex, mount: upgradedMount }));
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
          System Assignment
        </h3>

      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          <span className="font-medium">Hardpoints Available:</span>{" "}
          {availableHardpoints} / {hardpoints}
          {firmpoints > 0 && (
            <span>
              {" "}
              | <span className="font-medium">Firmpoints Available:</span>{" "}
              {availableFirmpoints} / {firmpoints}
            </span>
          )}
        </p>
      </div>

      {/* Simplified Mount Type Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Add New Mount
          </label>
          <select
            value={selectedMountType}
          onChange={(e) => handleMountTypeChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
        >
          <option value="">Choose type to add...</option>
          <option value="fixed">Fixed Mount</option>
            <option value="turret">Turret</option>
            <option value="barbette">Barbette</option>
            <option value="smallBay">Small Bay</option>
            <option value="mediumBay">Medium Bay</option>
            <option value="largeBay">Large Bay</option>
            <option value="spinalMount">Spinal Mount</option>
          <option value="pointDefenseBattery">Point Defence Battery</option>
          <option value="screen">Screen</option>
          </select>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Select a type to automatically add it to your ship. You can then configure weapons and settings directly in the summary below.
        </p>
        </div>

      {/* Weapon Mounts Summary */}
      {weaponMounts.length > 0 ? (
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-700 dark:text-gray-300">
            Installed Weapon Mounts ({weaponMounts.reduce((total, mount) => total + (mount.quantity || 1), 0)})
          </h4>
          {weaponMounts.map((mount, index) => (
            <WeaponSelector
              key={index}
              mount={mount}
              mountIndex={index}
              removeMount={() => dispatch(removeWeaponMount(index))}
              upgradeToTurret={
                mount.type === "fixed" && canUpgradeMountToTurret(mount.type, hullSize, firmpointUpgrades)
                  ? () => handleUpgradeFirmpointToTurret(index)
                  : undefined
              }
              canUpgradeToTurret={canUpgradeMountToTurret(mount.type, hullSize, firmpointUpgrades)}
            />
          ))}
          </div>
      ) : (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No weapon mounts installed yet.</p>
          <p className="text-sm mt-1">Select a mount type above to get started.</p>
        </div>
      )}

      {/* Summary Statistics */}
      {weaponMounts.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Weapon System Summary
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-600 dark:text-gray-400">Total Mounts:</span>
              <div className="text-gray-900 dark:text-gray-100">{weaponMounts.length}</div>
            </div>
            <div>
              <span className="font-medium text-gray-600 dark:text-gray-400">Hardpoints Used:</span>
              <div className="text-gray-900 dark:text-gray-100">{hardpointsUsed} / {hardpoints}</div>
            </div>
            <div>
              <span className="font-medium text-gray-600 dark:text-gray-400">Firmpoints Used:</span>
              <div className="text-gray-900 dark:text-gray-100">{firmpointsUsed} / {firmpoints}</div>
            </div>
            <div>
              <span className="font-medium text-gray-600 dark:text-gray-400">Total Cost:</span>
              <div className="text-gray-900 dark:text-gray-100">
                {formatCurrency(
                  weaponMounts.reduce((total, mount) => total + mount.cost, 0)
                )}
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-600 dark:text-gray-400">Total Weight:</span>
              <div className="text-gray-900 dark:text-gray-100">
                {weaponMounts.reduce((total, mount) => total + mount.tons, 0).toFixed(1)} tons
              </div>
            </div>
            <div>
              <span className="font-medium text-gray-600 dark:text-gray-400">Total Power:</span>
              <div className="text-gray-900 dark:text-gray-100">
                {weaponMounts.reduce((total, mount) => total + mount.power, 0)}
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkWeaponAssignment;